/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import _ from "underscore";
import FuzzySearch from "fz-search";
import analytics from "../analytics/analytics";
import baustein from "baustein";
import browser from "utils/browser";
import optionsTemplate from "templates/modules/category_search_options";
import resultsTemplate from "templates/modules/category_search_results";
import style from "../dom/style";
import template from "templates/modules/category_search";
import traversal from "../dom/traversal";

const componentName = "category-search";
const scrollableAreaComponentName = "scrollable-area";

const closeButtonSelector = `.${componentName}__close-btn`;
const contentContainerSelector = ".scrollable-area-content";
const dropDownSelector = `.${componentName}__drop-down`;
const hiddenProductTypeSelector = "input[name='product_type']";
const hiddenCategorySelector = "input[name='category']";
const hiddenSubcategorySelector = "input[name='subcategory']";
const inputSelector = `.${componentName}__search-input`;
const labelSelector = `.${componentName}__label`;
const resultClassName = `${componentName}__result`;
const resultActiveClassName = `${resultClassName}--active`;
const resultActiveSelector = `.${resultActiveClassName}`;
const resultSelector = `.${resultClassName}`;
const resultsContainerSelector = `.${componentName}__results-container`;
const scrollAreaSelector = ".scrollable-area-inner-wrapper";

export default baustein.register(componentName, {
    template,

    init() {
        // To indicate whether the user used the search
        this.startedTyping = false;
        this.searcher = {};
        this._initSearcher();
    },

    setupEvents(add) {
        add("click", labelSelector, this.onDropDownClick);
        add("click", resultSelector, this.onOptionClick);
        add("click", closeButtonSelector, this.onCloseClick);
        add("input", inputSelector, this.onInput);
        add("mouseenter", resultSelector, this.onMouseEnter);
        add("mouseleave", resultSelector, this.onMouseLeave);
        add("keydown", inputSelector, this.onKeyDown);
        this.setGlobalHandler("resize", _.debounce(this.onResize, 100));
    },

    /**
     * Update scroll area so that scroll parameters are correct
     */
    _updateScrollableArea() {
        const resultsContainer = this.el.querySelector(resultsContainerSelector);
        // Override scrollable-area.js inline height css so that we can change
        // the height of the scrollable area
        style.setStyle(resultsContainer, "height", "initial");
        this.findComponent(scrollableAreaComponentName).render();
    },

    /**
     * Initialise a searcher for each gender
     */
    _initSearcher() {
        Object.entries(this.options.groupedCategoriesMap).forEach(([gender, groupedCategories]) => {
            const flattenedOptions = groupedCategories
                .map(option => {
                    const [, categoryOptions] = option;
                    return categoryOptions;
                })
                .reduce((accumulatedCategories, currentCategories) => [
                    ...accumulatedCategories,
                    ...currentCategories
                ]);
            const flattenedOptionsWithSubcategories = [
                ...flattenedOptions,
                ...this.options.subcategoriesMap[gender]
            ];
            this.searcher[gender] = new FuzzySearch({
                source: flattenedOptionsWithSubcategories,
                keys: ["label"],
                // min char for suggestions to start showing
                token_field_min_length: 1
            });
        });
    },

    _updateGender(gender) {
        this.options.gender = gender;
    },

    toggleDropDown(force) {
        const isClosed = this.el.querySelector(dropDownSelector).classList.toggle("hidden", force);
        const inputElement = this.el.querySelector(inputSelector);
        if (isClosed) {
            inputElement.value = "";
            // Emit event to `lyst-app` to unblock scrolling on mobile
            if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
                // Scrolling block doesn't work properly on iOS still, we've to manually scroll
                // encouraged form back to view (which is also at the top of the page)
                browser.scrollTop(0, 0);
                this.emit("overlayClose");
            }
        } else {
            inputElement.focus();
            this.renderOptions();
            analytics.event("category search", "clicked", "drop down");
            this.emit("openedDropDown");
            // Emit event to `lyst-app` to block scrolling on mobile
            if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
                this.emit("overlayOpen");
            }
        }
    },

    closeDropDown() {
        this.toggleDropDown(true);
    },

    onCloseClick() {
        this.closeDropDown();
        this.emit("categorySearchCloseClicked");
    },

    onDropDownClick() {
        this.toggleDropDown();
    },

    onInput(event) {
        const inputValue = event.target.value;
        if (inputValue) {
            this.renderSearchResults(inputValue);
        } else {
            this.renderOptions();
        }
    },

    onOptionClick(_, target) {
        const selectedOption = traversal.closest(target, resultSelector);
        this._updateHiddenFields(selectedOption);
        const selectedOptionLabel = selectedOption.textContent.trim();
        this.el.querySelector(labelSelector).textContent = selectedOptionLabel;
        this.updateOptions(
            Object.assign(this.options, {
                selectedOptionLabel: selectedOptionLabel
            })
        );
        this.emit("categorySearchChanged");
        this.toggleDropDown();
    },

    _updateHiddenFields(selectedOption) {
        const hiddenProductType = this.el.querySelector(hiddenProductTypeSelector);
        hiddenProductType.value = selectedOption.dataset.productType;
        const hiddenCategory = this.el.querySelector(hiddenCategorySelector);
        hiddenCategory.value = selectedOption.dataset.category;
        const hiddenSubcategory = this.el.querySelector(hiddenSubcategorySelector);
        hiddenSubcategory.value = selectedOption.dataset.subcategory;
    },

    onGenderChange(gender) {
        this._updateGender(gender);
        const context = this.getRenderContext();
        const currentCategories = this.options.groupedCategoriesMap[this.options.gender];
        context.category_search.current_categories = currentCategories;
        let nonGroupedCategories;
        [[, nonGroupedCategories]] = currentCategories;
        const allCategoriesOption = nonGroupedCategories[0];
        const selectedOptionLabel = allCategoriesOption["label"];
        context.category_search.selected_option_label = selectedOptionLabel;
        this.setRenderContext(context);
        this.updateOptions(
            Object.assign(this.options, {
                currentCategories: currentCategories,
                selectedOptionLabel: selectedOptionLabel
            })
        );
    },

    onMouseEnter(event) {
        this._makeSuggestionActive(event.target);
    },

    onMouseLeave() {
        this._makeSuggestionActive(null);
    },

    onKeyDown(event) {
        if (!this.startedTyping) {
            // we only wanted to send this event once,
            // to tell if users are actually using the search input
            analytics.event("category search", "typed", "search input");
            this.startedTyping = true;
        }

        switch (event.keyCode) {
            // enter key
            case 13:
                const target = this._getActiveSuggestion();
                event.preventDefault();
                this.onOptionClick(null, target);
                break;
            // up key
            case 38:
                event.preventDefault();
                this._navigateActiveSuggestion("previous");
                break;
            // down key
            case 40:
                event.preventDefault();
                this._navigateActiveSuggestion("next");
                break;
        }
    },

    onResize() {
        this._updateScrollableArea();
    },

    _getActiveSuggestion() {
        return this.el.querySelector(resultActiveSelector);
    },

    _navigateActiveSuggestion(direction) {
        const activeSuggestion = this._getActiveSuggestion();
        const suggestions = Array.from(this.el.querySelectorAll(resultSelector));
        const currentIndex = suggestions.indexOf(activeSuggestion);
        let nextIndex;
        if (direction === "next") {
            // If current element is last, loop back to first element
            nextIndex = suggestions[currentIndex + 1] === undefined ? 0 : currentIndex + 1;
        } else {
            // if current element is first, loop back to last element
            nextIndex =
                suggestions[currentIndex - 1] === undefined
                    ? suggestions.length - 1
                    : currentIndex - 1;
        }
        const nextActiveSuggestion = suggestions[nextIndex];
        this._makeSuggestionActive(nextActiveSuggestion);
        this._updateScrollView(nextActiveSuggestion);
        const searchInput = this.el.querySelector(inputSelector);
        searchInput.value = nextActiveSuggestion.textContent.trim();
    },

    /**
     * Updates the scroll position to make sure that active suggestion is always within
     * the scroll view
     * @param activeSuggestion The latest active suggestion dom element
     */
    _updateScrollView(activeSuggestion) {
        const scrollArea = this.el.querySelector(scrollAreaSelector);

        const aboveScrollArea =
            activeSuggestion.offsetTop + activeSuggestion.offsetHeight <= scrollArea.scrollTop;
        const belowScrollArea =
            activeSuggestion.offsetTop + activeSuggestion.offsetHeight >
            scrollArea.scrollTop + scrollArea.offsetHeight;

        if (belowScrollArea) {
            scrollArea.scrollTop =
                activeSuggestion.offsetTop +
                activeSuggestion.offsetHeight -
                scrollArea.offsetHeight;
        } else if (aboveScrollArea) {
            scrollArea.scrollTop = activeSuggestion.offsetTop;
        }
    },

    /**
     * Scrolls to selected options, otherwise, to the top
     * @private
     */
    _scrollToSelectedOrTop() {
        const activeSuggestion = this._getActiveSuggestion();
        const scrollArea = this.el.querySelector(scrollAreaSelector);
        if (!activeSuggestion) {
            scrollArea.scrollTop = 0;
        } else {
            scrollArea.scrollTop = activeSuggestion.offsetTop;
        }
    },

    _makeSuggestionActive(target) {
        const suggestions = Array.from(this.el.querySelectorAll(resultSelector));
        suggestions.forEach(suggestion => {
            suggestion.classList.toggle(resultActiveClassName, suggestion === target);
        });
    },

    /**
     * Renders search results
     * @param inputValue: the query term
     */
    renderSearchResults(inputValue) {
        const searchResults = this.searcher[this.options.gender].search(inputValue);
        const formattedSearchResults = searchResults.map(result => {
            // this highlights input character in each label
            result.html_label = this.searcher[this.options.gender].highlight(result.label);
            return result;
        });
        const renderedResults = resultsTemplate({
            search_results: formattedSearchResults,
            query: inputValue
        });
        const contentContainer = this.el.querySelector(contentContainerSelector);
        contentContainer.innerHTML = renderedResults;
        this._updateScrollableArea();
        this._scrollToSelectedOrTop();
    },

    /**
     * Renders all category options
     */
    renderOptions() {
        const renderedOptions = optionsTemplate({
            current_categories: this.options.currentCategories,
            selected_option_label: this.options.selectedOptionLabel
        });
        const contentContainer = this.el.querySelector(contentContainerSelector);
        contentContainer.innerHTML = renderedOptions;
        this._updateScrollableArea();
        this._scrollToSelectedOrTop();
    },

    getInitialRenderContext() {
        return {
            category_search: {
                gender: this.options.gender,
                selected_option_label: this.options.selectedOptionLabel,
                current_categories: this.options.currentCategories,
                hidden_fields: this.options.hiddenFields
            }
        };
    }
});

